import { Link } from 'react-router-dom';
import usePageMeta from '../hooks/usePageMeta';
import { trackArtistGuideTermsClick } from '../services/analyticsService';
import './ArtistGuidePage.css';

const ArtistGuidePage = () => {
  usePageMeta({
    title: 'Checklist para tu perfil de artista — Bluppimart',
    description: 'Todo lo que necesitamos para armar tu perfil de artista en Bluppimart: contenido, medidas de imagen y video, datos de pago y las condiciones de la colaboración.',
  });

  return (
    <div className="guide-shell">
      <div className="guide-page">
        <section className="guide-welcome">
          <p className="guide-welcome-eyebrow">Guía para artistas</p>
          <h1 className="guide-welcome-title">Bienvenido a Bluppimart</h1>
          <p>
            Bluppimart es una plataforma de arte multidisciplinario que conecta a los artistas y estudios
            independientes con los coleccionistas. Cada artista tiene su propio perfil — con su biografía, su galería
            y sus piezas en venta — para que quienes lo visiten conozcan más de su trabajo.
          </p>
          <p>
            Crear y mantener tu perfil no tiene ningún costo y no cobramos comisión por tus ventas: los compradores te
            pagan directo a ti, por transferencia o con el link de pago de cada pieza.
          </p>
          <p>
            Esta guía reúne todo lo que necesitamos para publicar tu perfil: los textos, las imágenes y el video, tus
            datos para recibir pagos, la información de las piezas que quieras vender y las condiciones de la
            colaboración.
          </p>
          <p>
            Tenerlo todo listo desde el inicio nos permite armar tu página más rápido y que se vea lo mejor posible.
          </p>
        </section>

        {/* Collapsed by default — native <details>, no JS state. */}
        <details className="guide-letter">
          <summary className="guide-letter-summary">Mensaje del fundador</summary>
          <div className="guide-letter-marker" />
          <p>Hola a todos, este texto NO fue generado con IA.</p>
          <p>En días pasados les he compartido el objetivo y la visión de Bluppimart.</p>
          <p>
            Somos una plataforma de Art Toys que busca conectar a los artistas como ustedes, con los coleccionistas
            como yo, a través de una propuesta segura, digital e innovadora.
          </p>
          <p>
            Este proyecto nace desde el gusto y la pasión por los Art Toys, llevo aproximadamente 15 años
            coleccionando figuras y me hubiera gustado en ese entonces encontrar un sitio donde pudiera conocer el
            trabajo de los artistas, la historia de las piezas y por qué no, precios más accesibles.
          </p>
          <p>
            El principal motivo por el cual NO cobramos una comisión para crear tu perfil digital y vender a través
            de nuestra plataforma, es porque considero que al generar espacios que permitan la difusión de proyectos
            locales, la escena puede crecer y los artistas pueden seguir haciendo lo que les gusta.
          </p>
          <p>
            Vengo del mundo de las comunidades tecnológicas y mucho de lo que aprendí siempre fue gratuito, porque la
            mejor forma de aprender es compartir el conocimiento, es por eso que ahora que tengo algo de experiencia
            en el mundo de los negocios y de la tecnología, estoy creando esto para ustedes.
          </p>
          <p>
            Gracias a los que han confiado desde el día 1, seguiré trabajando y disfrutando de este viaje llamado
            emprender.
          </p>
          <p className="guide-letter-closing">Sinceramente,</p>
          <p className="guide-letter-signature">Humberto Solano</p>
        </details>

        <header className="guide-header">
          <h2 className="guide-title">Checklist para tu perfil de artista</h2>
        </header>

        <p className="guide-intro">
          Si estás interesado en formar parte de este proyecto, a continuación te compartimos la lista de
          requerimientos. Puedes enviarnos la información en un archivo de texto o por correo electrónico a{' '}
          <a href="mailto:hey@bluppimart.com">hey@bluppimart.com</a>.
        </p>

        <p className="guide-intro">
          Con este contenido armamos tu perfil de artista, con tu historia, tu galería y, si estás interesado, tus
          piezas en venta.
        </p>

        <p className="guide-intro">
          <strong>Entre más completa esté la lista, mejor se verá tu perfil.</strong>
        </p>

        <section className="guide-section">
          <h2 className="guide-part-title">Parte 1 — Lo que necesitamos de ti</h2>
          <p className="guide-section-note">
            Marcado como <span className="guide-tag guide-tag-req">Obligatorio</span> es lo mínimo para publicar tu
            perfil. Lo demás solo mejora tu página.
          </p>

          <p className="guide-group-label">Identidad</p>
          <ul className="guide-list">
            <li className="guide-item">
              <div className="guide-item-num">01</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Nombre de tu marca o proyecto <span className="guide-tag guide-tag-req">Obligatorio</span>
                </div>
                <div className="guide-item-desc">El nombre con el que se identifica tu proyecto en Bluppimart.</div>
                <div className="guide-item-spec">≤ 35 caracteres</div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">02</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Tu nombre, si quieres que aparezca <span className="guide-tag guide-tag-opt">Opcional</span>
                </div>
                <div className="guide-item-desc">
                  Se muestra como "por [tu nombre]" junto al nombre del proyecto.
                </div>
                <div className="guide-item-spec">≤ 35 caracteres</div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">03</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Instagram <span className="guide-tag guide-tag-opt">Recomendado</span>
                </div>
                <div className="guide-item-desc">El que quieras mostrar como principal en tu perfil.</div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">04</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Correo para avisos de contacto con personas interesadas, ventas y pagos{' '}
                  <span className="guide-tag guide-tag-req">Obligatorio</span>
                </div>
                <div className="guide-item-desc">
                  Aquí te avisamos cuando alguien se interesa en tu perfil y cuando se vende una pieza tuya, con el
                  monto que vas a recibir y cómo te van a pagar.
                </div>
              </div>
            </li>
          </ul>

          <p className="guide-group-label">Pagos</p>
          <ul className="guide-list">
            <li className="guide-item">
              <div className="guide-item-num">05</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Cuenta bancaria: CLABE interbancaria, banco y nombre del titular{' '}
                  <span className="guide-tag guide-tag-req">Obligatorio si vendes piezas</span>
                </div>
                <div className="guide-item-desc">
                  Aquí recibes las transferencias de los compradores que elijan pagarte directo. Solo la ve el equipo
                  de Bluppimart — nunca se muestra en tu perfil. Al compartirla nos autorizas a guardarla y a dársela a
                  quien compre tus piezas para que te pague, conforme al{' '}
                  <Link to="/terminos-y-condiciones">Aviso de Privacidad</Link>.
                </div>
                <div className="guide-item-spec">CLABE de 18 dígitos</div>
              </div>
            </li>
          </ul>

          <p className="guide-group-label">Historia</p>
          <ul className="guide-list">
            <li className="guide-item">
              <div className="guide-item-num">06</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Biografía del proyecto <span className="guide-tag guide-tag-req">Obligatorio</span>
                </div>
                <div className="guide-item-desc">
                  Qué es el proyecto, de dónde viene, qué lo hace distinto. Escríbela corta: se lee completa sin
                  necesidad de expandirla.
                </div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">07</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Biografía del artista <span className="guide-tag guide-tag-opt">Opcional</span>
                </div>
                <div className="guide-item-desc">
                  Solo si elegiste que tu nombre aparezca. Se muestra como "sobre el creador", junto a la bio del
                  proyecto.
                </div>
              </div>
            </li>
          </ul>

          <p className="guide-group-label">Imágenes</p>
          <ul className="guide-list">
            <li className="guide-item">
              <div className="guide-item-num">08</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Foto de perfil <span className="guide-tag guide-tag-req">Obligatorio</span>
                </div>
                <div className="guide-item-desc">
                  Se muestra en círculo junto a tu bio. Céntrala en el rostro o el logo — los bordes se recortan.
                </div>
                <div className="guide-item-spec">Cuadrada 1:1 · mín. 500×500 px</div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">09</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  3 imágenes verticales — tu carta de presentación{' '}
                  <span className="guide-tag guide-tag-req">Obligatorio (mín. 1)</span>
                </div>
                <div className="guide-item-desc">
                  Ilustraciones, fotos de producto, diseños, bocetos, pinturas — lo que quieras mostrar primero. Se
                  recortan a 3:4, así que cuida que lo importante quede centrado, no en los bordes.
                </div>
                <div className="guide-item-spec">Vertical 3:4 · mín. 1200×1600 px</div>
              </div>
            </li>
          </ul>

          <p className="guide-group-label">Video</p>
          <ul className="guide-list">
            <li className="guide-item">
              <div className="guide-item-num">10</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Video corto de tu proceso <span className="guide-tag guide-tag-req">Obligatorio</span>
                </div>
                <div className="guide-item-desc">
                  Diseño, modelado, lijado, pintura — cualquier parte del proceso que quieras compartir. Ideal en
                  loop, que el final conecte con el inicio. MP4, MOV o WEBM, máx. 100 MB.
                </div>
                <div className="guide-item-spec">Vertical 9:16 · 1080×1920 px · 6–12 seg</div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">11</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Texto que acompaña al video <span className="guide-tag guide-tag-opt">Opcional</span>
                </div>
                <div className="guide-item-desc">Descripción o mensaje corto sobre lo que se ve en el video.</div>
                <div className="guide-item-spec">≤ 400 caracteres</div>
              </div>
            </li>
          </ul>

          <p className="guide-group-label">Color</p>
          <ul className="guide-list">
            <li className="guide-item">
              <div className="guide-item-num">12</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Color de fondo de tu perfil <span className="guide-tag guide-tag-opt">Recomendado</span>
                </div>
                <div className="guide-item-desc">
                  El color que va detrás en tu página. Envíanoslo como código hex (ej. #1F2937) — si no nos
                  compartes uno, usamos un color por defecto.
                </div>
              </div>
            </li>
          </ul>
        </section>

        <section className="guide-section">
          <h2 className="guide-part-title">Parte 2 — Tus piezas en venta</h2>
          <p className="guide-section-note">
            Si te interesa subir algunas piezas, necesitamos tu cuenta bancaria (punto 05) para poder venderlas. Los
            requerimientos por cada pieza son:
          </p>

          <ul className="guide-list">
            <li className="guide-item">
              <div className="guide-item-num">13</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Nombre de la pieza <span className="guide-tag guide-tag-req">Obligatorio</span>
                </div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">14</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Imágenes <span className="guide-tag guide-tag-req">Obligatorio</span>
                </div>
                <div className="guide-item-desc">De preferencia frente y vuelta.</div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">15</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Descripción <span className="guide-tag guide-tag-req">Obligatorio</span>
                </div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">16</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Precio <span className="guide-tag guide-tag-req">Obligatorio</span>
                </div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">17</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Link de pago de Mercado Pago o Clip <span className="guide-tag guide-tag-opt">Recomendado</span>
                </div>
                <div className="guide-item-desc">
                  Un link por pieza, por el mismo monto que el precio de la pieza en Bluppimart.
                </div>
                <div className="guide-item-spec">https://mpago.la/… · https://link.mercadopago.com.mx/… · https://pago.clip.mx/…</div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">18</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Dimensiones <span className="guide-tag guide-tag-opt">Recomendado</span>
                </div>
              </div>
            </li>
          </ul>
        </section>

        <section className="guide-section">
          <h2 className="guide-part-title">Parte 3 — Tu historial de customs</h2>
          <p className="guide-section-note">
            Opcional. Si has participado en customs, shows o eventos y quieres mostrar esa historia en tu perfil, los
            requerimientos por cada entrada son:
          </p>

          <ul className="guide-list">
            <li className="guide-item">
              <div className="guide-item-num">19</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Año <span className="guide-tag guide-tag-req">Obligatorio</span>
                </div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">20</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Nombre del evento <span className="guide-tag guide-tag-opt">Opcional</span>
                </div>
                <div className="guide-item-desc">Ej. "Toy Con MX 2024".</div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">21</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Plataforma (figura base) <span className="guide-tag guide-tag-req">Obligatorio</span>
                </div>
                <div className="guide-item-desc">Ej. "Bearbrick 400%".</div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">22</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Materiales <span className="guide-tag guide-tag-req">Obligatorio</span>
                </div>
                <div className="guide-item-desc">Ej. "Resina, acrílico".</div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">23</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Imágenes del custom <span className="guide-tag guide-tag-req">Obligatorio (mín. 1)</span>
                </div>
                <div className="guide-item-desc">Hasta 4. La primera es la principal.</div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">24</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Nombre del custom <span className="guide-tag guide-tag-opt">Opcional</span>
                </div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">25</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Creador de la plataforma <span className="guide-tag guide-tag-opt">Opcional</span>
                </div>
                <div className="guide-item-desc">Ej. "Medicom Toy".</div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">26</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Mes <span className="guide-tag guide-tag-opt">Opcional</span>
                </div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">27</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Historia <span className="guide-tag guide-tag-opt">Opcional</span>
                </div>
                <div className="guide-item-desc">Breve historia detrás de esta pieza o evento.</div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">28</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Dimensiones <span className="guide-tag guide-tag-opt">Opcional</span>
                </div>
              </div>
            </li>
            <li className="guide-item">
              <div className="guide-item-num">29</div>
              <div className="guide-item-body">
                <div className="guide-item-title">
                  Precio en MXN <span className="guide-tag guide-tag-opt">Opcional</span>
                </div>
                <div className="guide-item-desc">
                  Se puede marcar como vendido/agotado para ocultar el precio en el perfil público.
                </div>
              </div>
            </li>
          </ul>
        </section>

        <section className="guide-section">
          <h2 className="guide-part-title">Parte 4 — Condiciones de la colaboración</h2>
          <p className="guide-section-note">Dos puntos que queremos que te queden claros antes de compartir tu material.</p>

          <div className="guide-terms-grid">
            <div className="guide-term-card">
              <div className="guide-term-marker" />
              <h3>Propiedad intelectual</h3>
              <p>
                Tú conservas todos los derechos sobre tus personajes, diseños e ilustraciones — eso no cambia por
                estar en Bluppimart.
              </p>
              <p>
                Al compartir tus imágenes, video y textos, nos das permiso para usarlos únicamente dentro de la
                plataforma: tu perfil de artista, el catálogo y la promoción de tus piezas en Bluppimart y sus redes. No los
                usamos fuera de eso, ni se los damos a terceros.
              </p>
              <p>
                Términos completos:{' '}
                <Link to="/terminos-y-condiciones" onClick={trackArtistGuideTermsClick}>
                  bluppimart.com/terminos-y-condiciones
                </Link>
              </p>
            </div>
            <div className="guide-term-card">
              <div className="guide-term-marker" />
              <h3>Comisión y pagos</h3>
              <p>
                No cobramos ninguna cuota por crear, mantener o actualizar tu perfil, ni comisión por tus ventas.
              </p>
              <p>
                El comprador te paga directo a ti — por transferencia a tu CLABE o con el link de Mercado Pago o Clip
                de la pieza. Bluppimart no retiene el dinero. Mercado Pago y Clip cobran su propia comisión por los
                pagos con link.
              </p>
              <p>
                Cuando alguien compra una pieza tuya te avisamos por correo con el monto que vas a recibir;
                confírmanos cuando te llegue el pago para preparar la entrega.
              </p>
              <p>
                Si ya estás en Bluppimart y aún no nos compartes tus datos de pago, tus piezas se siguen cobrando con
                tarjeta (comisión de Stripe de <span className="guide-fillin">4.5% + IVA</span>) y te transferimos tu
                pago cuando Stripe nos libera el dinero, hasta que nos envíes tus datos.
              </p>
              <p>
                Tus datos bancarios se tratan conforme al{' '}
                <Link to="/terminos-y-condiciones">Aviso de Privacidad</Link>.
              </p>
            </div>
          </div>
        </section>

        <p className="guide-footer">
          ¿Dudas sobre algún punto? Escríbenos a <a href="mailto:hey@bluppimart.com">hey@bluppimart.com</a>.
        </p>

        <section className="guide-changelog">
          <h2 className="guide-changelog-title">Historial de actualizaciones</h2>
          <ul className="guide-changelog-list">
            <li>
              <span className="guide-changelog-date">03 nov 2026</span>
              Se agregó la solicitud de datos bancarios y el link de pago de Mercado Pago o Clip para la venta de piezas.
            </li>
            <li>
              <span className="guide-changelog-date">14 sep 2026</span>
              Se agregó un mensaje del fundador, el requisito de color de fondo, la sección de piezas en venta y la sección de historial de customs.
            </li>
            <li>
              <span className="guide-changelog-date">07 jul 2026</span>
              Primera versión de la guía.
            </li>
          </ul>
        </section>
      </div>
    </div>
  );
};

export default ArtistGuidePage;
